import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioPlayerContext } from './context/AudioPlayerContext';
import Previous from '../../assets/icons/audioplayer/previous.svg';
import Backward from '../../assets/icons/audioplayer/backward.svg';
import Forward from '../../assets/icons/audioplayer/forward.svg';
import Next from '../../assets/icons/audioplayer/next.svg';
import Pause from '../../assets/icons/audioplayer/pause.svg';
import Play from '../../assets/icons/audioplayer/play.svg';
import { motion } from 'framer-motion';

interface ControlsProps {
  onPlayPause?: () => void;
  isMobileView?: boolean;
  isPlaying?: boolean;
  setIsPlaying?: (value: boolean | ((prev: boolean) => boolean)) => void;
}

export const Controls: React.FC<ControlsProps> = ({ 
  onPlayPause, 
  isMobileView = false,
  isPlaying: propIsPlaying,
  setIsPlaying: propSetIsPlaying
}) => {
  const {
    currentTrack,
    tracks,
    audioRef,
    setDuration,
    duration,
    timeProgress,
    setTimeProgress,
    progressBarRef,
    setTrackIndex,
    setCurrentTrack,
    isPlaying: contextIsPlaying,
    setIsPlaying: contextSetIsPlaying,
  } = useAudioPlayerContext();

  const isPlaying = propIsPlaying ?? contextIsPlaying;
  const setIsPlaying = propSetIsPlaying ?? contextSetIsPlaying;

  const [isShuffle, setIsShuffle] = useState<boolean>(false);
  const [isRepeat, setIsRepeat] = useState<boolean>(false);

  const playAnimationRef = useRef<number | null>(null);

  const updateProgress = useCallback(() => {
    if (audioRef.current && progressBarRef.current && duration) {
      const currentTime = audioRef.current.currentTime;
      setTimeProgress(currentTime);

      progressBarRef.current.value = currentTime.toString();
      progressBarRef.current.style.setProperty(
        '--range-progress',
        `${(currentTime / duration) * 100}%`
      );
    }
  }, [duration, setTimeProgress, audioRef, progressBarRef]);

  const startAnimation = useCallback(() => {
    if (audioRef.current && progressBarRef.current && duration) {
      const animate = () => {
        updateProgress();
        playAnimationRef.current = requestAnimationFrame(animate);
      };
      playAnimationRef.current = requestAnimationFrame(animate);
    }
  }, [updateProgress, duration, audioRef, progressBarRef]);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current?.play();
      startAnimation();
    } else {
      audioRef.current?.pause();
      if (playAnimationRef.current !== null) {
        cancelAnimationFrame(playAnimationRef.current);
        playAnimationRef.current = null;
      }
      updateProgress();
    }

    return () => {
      if (playAnimationRef.current !== null) {
        cancelAnimationFrame(playAnimationRef.current);
      }
    };
  }, [isPlaying, startAnimation, updateProgress, audioRef]);

  const onLoadedMetadata = () => {
    const seconds = audioRef.current?.duration;
    if (seconds !== undefined) {
      setDuration(seconds);
      if (progressBarRef.current) {
        progressBarRef.current.max = seconds.toString();
      }
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 15;
      updateProgress();
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 15;
      updateProgress();
    }
  };

  const handlePrevious = useCallback(() => {
    if (tracks.length === 0) return;
  
    setTrackIndex((prev) => {
      const newIndex = isShuffle
        ? Math.floor(Math.random() * tracks.length)
        : prev === 0
          ? tracks.length - 1
          : prev - 1;
      setCurrentTrack(tracks[newIndex]);
      return newIndex;
    });
  }, [isShuffle, tracks, setCurrentTrack, setTrackIndex]);
  
  const handleNext = useCallback(() => {
    if (tracks.length === 0) return;
  
    setTrackIndex((prev) => {
      const newIndex = isShuffle
        ? Math.floor(Math.random() * tracks.length)
        : prev >= tracks.length - 1
          ? 0
          : prev + 1;
      setCurrentTrack(tracks[newIndex]);
      return newIndex;
    });
  }, [isShuffle, tracks, setCurrentTrack, setTrackIndex]);

  useEffect(() => {
    const currentAudioRef = audioRef.current;

    if (currentAudioRef) {
      currentAudioRef.onended = () => {
        if (isRepeat) {
          currentAudioRef.play();
        } else {
          handleNext();
        }
      };
    }

    return () => {
      if (currentAudioRef) {
        currentAudioRef.onended = null;
      }
    };
  }, [isRepeat, handleNext, audioRef]);

  const formatTime = (time: number | undefined): string => {
    if (typeof time === 'number' && !isNaN(time)) {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return '0:00';
  };

  return (
    <motion.div 
      className="flex gap-4 items-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        y: { type: "spring", stiffness: 100, damping: 15 }
      }}
    >
      {isMobileView && (
        <motion.button 
          className='sm:flex'
          onClick={handlePrevious}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src={Previous} srcSet={`${Previous} 1x, ${Previous} 2x`} loading="lazy" alt="Previous" title="Go to Previous" className="w-10 h-10" />
        </motion.button>
      )}
      {isMobileView && (
        <motion.button 
          className='sm:flex'
          onClick={skipBackward}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src={Backward} srcSet={`${Backward} 1x, ${Backward} 2x`} loading="lazy" alt="Backward" title="Go back 15 seconds" className="w-6 h-auto" />
        </motion.button>
      )}
      <motion.button
        className='rounded-full p-4 shadow-md'
        style={{ background: 'linear-gradient(180deg, rgba(212, 199, 199, 0.2) 0%, rgba(65, 64, 64, 0.2) 100%)' }}
        onClick={() => setIsPlaying((prev) => !prev)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: isPlaying ? [1, 1.1, 1] : 1,
          rotate: isPlaying ? [0, -5, 5, -5, 0] : 0
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
        {isPlaying ? (
          <img src={Pause} srcSet={`${Pause} 1x, ${Pause} 2x`} loading="lazy" alt="Pause" title="Pause" className="w-5 h-5" />
        ) : (
          <img src={Play} srcSet={`${Play} 1x, ${Play} 2x`} loading="lazy" alt="Play" title="Play" className="w-5 h-5" />
        )}
      </motion.button>
      {isMobileView && (
        <motion.button 
          className='sm:flex'
          onClick={skipForward}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src={Forward} srcSet={`${Forward} 1x, ${Forward} 2x`} loading="lazy" alt="Forward" title="Go Forward 15 seconds" className="w-6 h-auto" />
        </motion.button>
      )}
      {isMobileView && (
        <motion.button 
          className='sm:flex'
          onClick={handleNext}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src={Next} srcSet={`${Next} 1x, ${Next} 2x`} loading="lazy" alt="Next" title="Go to Next" className="w-10 h-10" />
        </motion.button>
      )}
      <motion.span 
        className='text-[#D0D5DD] text-sm md:text-base font-medium font-kanit hidden sm:flex sm:w-[55px] md:w-[73px]'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {formatTime(timeProgress)}/{formatTime(duration)}
      </motion.span>
    </motion.div>
  );
};
